import React, { useState, useEffect, useCallback, useRef } from 'react';
import './BountyBoardPanel.css';

const API = process.env.REACT_APP_SERVER_URL || '';

/**
 * BountyBoardPanel — Daily & Weekly monster hunt bounties
 * 
 * Accessed at the Tavern bounty board tile (antaria6).
 * Shows 3 daily + 1 weekly bounty scaled to the player's level.
 * Fellowship members share kill credit automatically.
 */
const BountyBoardPanel = ({ character, area, onClose, onCharacterUpdate, onOpenGuide }) => {
  const [daily, setDaily] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [stats, setStats] = useState({ completed: 0, totalXp: 0, totalGold: 0 });
  const [dailyResetIn, setDailyResetIn] = useState(0);
  const [weeklyResetIn, setWeeklyResetIn] = useState(0);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('daily'); // 'daily' | 'weekly'
  const [tavernName, setTavernName] = useState('Bounty Board');
  const lastRefreshRef = useRef(null); // Track if bounties refreshed since last visit
  const [actionBusy, setActionBusy] = useState(false);

  const token = localStorage.getItem('token');

  // Format milliseconds as "Xh Ym"
  const formatTime = (ms) => {
    if (ms <= 0) return 'Now';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const fetchBounties = useCallback(async () => {
    try {
      setLoading(true);
      const areaParam = area ? `?area=${encodeURIComponent(area)}` : '';
      const res = await fetch(`${API}/api/bounty${areaParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load bounties');
      const data = await res.json();
      setDaily(data.daily || []);
      setWeekly(data.weekly || null);
      setStats(data.stats || { completed: 0, totalXp: 0, totalGold: 0 });
      setDailyResetIn(data.dailyResetIn || 0);
      setWeeklyResetIn(data.weeklyResetIn || 0);
      if (data.tavernName) setTavernName(data.tavernName);

      // Welcome-back: detect if bounties refreshed since last visit
      const hasNoneAccepted = (data.daily || []).every(b => !b.accepted) &&
                              (!data.weekly || !data.weekly.accepted);
      if (lastRefreshRef.current === null) {
        // First load — check if all bounties are fresh (none accepted = new rotation)
        if (hasNoneAccepted && (data.stats?.completed || 0) > 0) {
          setMessage({ type: 'success', text: '🌅 Fresh bounties are here! New daily and weekly hunts await.' });
        }
        lastRefreshRef.current = { daily: data.dailyResetIn, weekly: data.weeklyResetIn };
      }
    } catch (err) {
      console.error('Error fetching bounties:', err);
      setMessage({ type: 'error', text: 'Failed to load bounty board.' });
    } finally {
      setLoading(false);
    }
  }, [token, area]);

  useEffect(() => {
    fetchBounties();
  }, [fetchBounties]);

  // Update countdown every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setDailyResetIn(prev => Math.max(0, prev - 60000));
      setWeeklyResetIn(prev => Math.max(0, prev - 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const acceptBounty = async (bountyId) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${API}/api/bounty/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bountyId })
      });
      const data = await res.json();
      if (data.error) {
        setMessage({ type: 'error', text: data.error });
        return;
      }
      setMessage({ type: 'success', text: 'Bounty accepted! Hunt begins.' });
      fetchBounties();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to accept bounty.' });
    } finally {
      setActionBusy(false);
    }
  };

  const claimReward = async (bountyId) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${API}/api/bounty/claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bountyId })
      });
      const data = await res.json();
      if (data.error) {
        setMessage({ type: 'error', text: data.error });
        return;
      }
      setMessage({
        type: 'success',
        text: `Bounty complete! +${data.rewards.xp} XP, +${data.rewards.gold} Gold${data.rewards.forgeTokens ? `, +${data.rewards.forgeTokens} Forge Tokens` : ''}`
      });
      if (data.stats) setStats(data.stats);
      if (onCharacterUpdate) onCharacterUpdate();
      fetchBounties();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to claim reward.' });
    } finally {
      setActionBusy(false);
    }
  };

  const getBountyStatus = (bounty) => {
    if (bounty.claimed) return 'claimed';
    if (bounty.accepted && bounty.current >= bounty.target) return 'complete';
    if (bounty.accepted) return 'active';
    return 'available';
  };

  const renderBountyCard = (bounty, type) => {
    const status = getBountyStatus(bounty);
    const progress = bounty.accepted ? Math.min(bounty.current / bounty.target, 1) : 0;

    return (
      <div key={bounty.id} className={`bounty-card bounty-${status} ${type === 'weekly' ? 'bounty-weekly-card' : ''}`}>
        <div className="bounty-card-header">
          <div className="bounty-monster-info">
            <img
              src={`/tiles/${bounty.monsterPic}`}
              alt={bounty.monster}
              className="bounty-monster-pic"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div className="bounty-monster-name">{bounty.monster}</div>
              <div className="bounty-monster-level">Level {bounty.monsterLevel}</div>
            </div>
          </div>
          <div className="bounty-type-badge">
            {type === 'weekly' ? '📜 WEEKLY' : '📋 DAILY'}
          </div>
        </div>

        <div className="bounty-objective">
          Defeat <strong>{bounty.target}</strong> {bounty.monster}{bounty.target > 1 ? 's' : ''}
        </div>

        {bounty.accepted && !bounty.claimed && (
          <div className="bounty-progress-container">
            <div className="bounty-progress-bar">
              <div
                className={`bounty-progress-fill ${status === 'complete' ? 'bounty-progress-complete' : ''}`}
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <div className="bounty-progress-text">
              {bounty.current} / {bounty.target}
            </div>
          </div>
        )}

        <div className="bounty-rewards">
          <span className="bounty-reward-xp">⭐ {bounty.rewards.xp} XP</span>
          <span className="bounty-reward-gold">💰 {bounty.rewards.gold} Gold</span>
          {bounty.rewards.forgeTokens > 0 && (
            <span className="bounty-reward-forge">🔨 {bounty.rewards.forgeTokens} Forge</span>
          )}
        </div>

        <div className="bounty-actions">
          {status === 'available' && (
            <button className="bounty-btn bounty-btn-accept" onClick={() => acceptBounty(bounty.id)}>
              Accept Bounty
            </button>
          )}
          {status === 'active' && (
            <span className="bounty-status-text bounty-status-hunting">🗡️ Hunting...</span>
          )}
          {status === 'complete' && (
            <button className="bounty-btn bounty-btn-claim" onClick={() => claimReward(bounty.id)}>
              ✨ Claim Reward
            </button>
          )}
          {status === 'claimed' && (
            <span className="bounty-status-text bounty-status-done">✅ Completed</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bounty-overlay" onClick={onClose}>
      <div className="bounty-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bounty-header">
          <div className="bounty-header-left">
            <h2>📜 Bounty Board</h2>
            <span className="bounty-subtitle">{tavernName}</span>
            {onOpenGuide && (
              <button
                type="button"
                onClick={() => onOpenGuide('challenge-loops')}
                style={{
                  alignSelf: 'flex-start',
                  marginTop: '8px',
                  background: 'rgba(212, 175, 55, 0.12)',
                  border: '1px solid rgba(212, 175, 55, 0.28)',
                  color: '#f0d58c',
                  borderRadius: '999px',
                  padding: '4px 10px',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
              >
                Guide: bounties & rewards
              </button>
            )}
          </div>
          <div className="bounty-header-right">
            <div className="bounty-lifetime-stats" title="Lifetime bounties completed">
              🏆 {stats.completed} completed
            </div>
            <button className="bounty-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bounty-tabs">
          <button
            className={`bounty-tab ${tab === 'daily' ? 'bounty-tab-active' : ''}`}
            onClick={() => setTab('daily')}
          >
            Daily Hunts
            <span className="bounty-reset-timer">↻ {formatTime(dailyResetIn)}</span>
          </button>
          <button
            className={`bounty-tab ${tab === 'weekly' ? 'bounty-tab-active' : ''}`}
            onClick={() => setTab('weekly')}
          >
            Weekly Challenge
            <span className="bounty-reset-timer">↻ {formatTime(weeklyResetIn)}</span>
          </button>
        </div>

        {/* Content */}
        <div className="bounty-content">
          {loading ? (
            <div className="bounty-loading">Loading bounties...</div>
          ) : tab === 'daily' ? (
            <div className="bounty-list">
              {/* Carry-over bounties from previous rotation */}
              {daily.filter(b => b.carryOver).length > 0 && (
                <>
                  <div className="bounty-section-header">⏳ Previous Bounties</div>
                  {daily.filter(b => b.carryOver).map(b => renderBountyCard(b, b.id?.startsWith('w_') ? 'weekly' : 'daily'))}
                </>
              )}
              {/* Fresh daily bounties */}
              {daily.filter(b => !b.carryOver).length > 0 ? (
                daily.filter(b => !b.carryOver).map(b => renderBountyCard(b, 'daily'))
              ) : (
                <div className="bounty-empty">No daily bounties available.</div>
              )}
              <div className="bounty-fellowship-note">
                💡 <em>Fellowship members share kill credit!</em>
              </div>
            </div>
          ) : (
            <div className="bounty-list">
              {weekly ? (
                renderBountyCard(weekly, 'weekly')
              ) : (
                <div className="bounty-empty">No weekly bounty available.</div>
              )}
              <div className="bounty-fellowship-note">
                💡 <em>Fellowship members share kill credit!</em>
              </div>
            </div>
          )}
        </div>

        {/* Message bar */}
        {message && (
          <div className={`bounty-message bounty-message-${message.type}`}>
            {message.text}
            <button className="bounty-message-dismiss" onClick={() => setMessage(null)}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BountyBoardPanel;
